import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { 
  Calculator, ArrowLeft, ArrowRight, Briefcase, Clock, 
  CheckCircle, HelpCircle, TrendingDown, Wallet, PiggyBank, Building
} from "lucide-react";

// Schema.org structured data
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Stipendio in Ticino: Dal Lordo al Netto e l'Imposta alla Fonte",
  "description": "Calcola il tuo stipendio netto in Svizzera. Guida alle trattenute AVS, LPP e imposta alla fonte per lavoratori stranieri in Canton Ticino.",
  "author": { "@type": "Organization", "name": "LavoroTicino.ch" },
  "publisher": { "@type": "Organization", "name": "LavoroTicino.ch" },
  "datePublished": "2026-01-15",
  "dateModified": "2026-01-15",
  "mainEntityOfPage": { "@type": "WebPage", "@id": "https://lavoroticino.ch/guide/stipendio-netto" },
  "keywords": "Stipendio Netto Svizzera, Imposta alla fonte, Calcolo stipendio Ticino, AVS LPP"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Quanto è l'imposta alla fonte in Ticino?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "L'imposta alla fonte in Canton Ticino varia dal 4% al 15% circa, a seconda dello stipendio lordo, stato civile e numero di figli. I frontalieri italiani pagano circa il 5-8% grazie all'accordo fiscale Italia-Svizzera."
      }
    },
    {
      "@type": "Question",
      "name": "Cosa sono AVS e LPP?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "L'AVS è l'assicurazione vecchiaia e superstiti (1° pilastro), mentre la LPP è la previdenza professionale (2° pilastro). Insieme formano la base del sistema pensionistico svizzero. Il lavoratore paga circa il 5.3% per AVS e il 7-8% per LPP."
      }
    },
    {
      "@type": "Question",
      "name": "Come si calcola lo stipendio netto in Svizzera?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Dallo stipendio lordo si sottraggono: AVS/AI/IPG (5.3%), AD (1.1%), LPP (7-8%), LAINF, e per stranieri l'imposta alla fonte (4-15%). Il netto è circa il 75-85% del lordo a seconda dei casi."
      }
    }
  ]
};

export default function StipendioNettoGuide() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify([articleSchema, faqSchema]);
    document.head.appendChild(script);
    window.scrollTo(0, 0);
    return () => document.head.removeChild(script);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/guide-lavoro" className="hover:text-[#004E64]">Guide</Link>
            <span>/</span>
            <span className="text-slate-900">Stipendio Netto</span>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <header className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Calculator className="w-6 h-6" />
            </div>
            <span className="text-sm font-medium text-white/80 uppercase tracking-wider">
              Guida al Calcolo
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Stipendio in Ticino:<br />
            Dal Lordo al Netto
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-6">
            Come calcolare il tuo stipendio netto in Svizzera. Guida completa alle trattenute 
            sociali AVS, LPP e all'imposta alla fonte per lavoratori stranieri.
          </p>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> 7 min di lettura
            </span>
            <span>•</span>
            <span>Aggiornato: Gennaio 2026</span>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-slate max-w-none">
            
            {/* Intro */}
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              Capire lo <strong>stipendio netto in Svizzera</strong> può sembrare complesso. 
              In questa guida ti spieghiamo tutte le <strong>trattenute sociali</strong> e 
              l'<strong>imposta alla fonte</strong> che verranno applicate al tuo salario lordo 
              in Canton Ticino.
            </p>

            {/* Section 1 - Sistema svizzero */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
              Il Sistema dei 3 Pilastri
            </h2>
            
            <p>
              Il sistema previdenziale svizzero si basa su <strong>3 pilastri</strong>. Come lavoratore, 
              contribuirai ai primi due tramite trattenute dallo stipendio:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
              <div className="bg-blue-50 p-5 rounded-xl text-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">1°</div>
                <h3 className="font-bold text-blue-900 mb-1">AVS/AI/IPG</h3>
                <p className="text-sm text-blue-700 m-0">Previdenza statale obbligatoria</p>
              </div>
              <div className="bg-emerald-50 p-5 rounded-xl text-center">
                <div className="w-12 h-12 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">2°</div>
                <h3 className="font-bold text-emerald-900 mb-1">LPP</h3>
                <p className="text-sm text-emerald-700 m-0">Cassa pensione professionale</p>
              </div>
              <div className="bg-purple-50 p-5 rounded-xl text-center">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">3°</div>
                <h3 className="font-bold text-purple-900 mb-1">Pilastro 3a</h3>
                <p className="text-sm text-purple-700 m-0">Risparmio volontario</p>
              </div>
            </div>

            {/* Section 2 - Deductions */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
              Le Trattenute Sociali
            </h2>

            <p>
              Ecco le principali trattenute che verranno applicate al tuo stipendio lordo:
            </p>

            <div className="overflow-x-auto mb-8 mt-6">
              <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="px-4 py-3 text-left font-semibold">Trattenuta</th>
                    <th className="px-4 py-3 text-left font-semibold">Descrizione</th>
                    <th className="px-4 py-3 text-center font-semibold">% Lavoratore</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 font-medium">AVS/AI/IPG</td>
                    <td className="px-4 py-3 text-sm">Vecchiaia, invalidità, perdita guadagno</td>
                    <td className="px-4 py-3 text-center font-bold text-red-600">5.30%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">AD (Disoccupazione)</td>
                    <td className="px-4 py-3 text-sm">Assicurazione contro la disoccupazione</td>
                    <td className="px-4 py-3 text-center font-bold text-red-600">1.10%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 font-medium">LPP (2° pilastro)</td>
                    <td className="px-4 py-3 text-sm">Cassa pensione professionale</td>
                    <td className="px-4 py-3 text-center font-bold text-red-600">7-8%*</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">LAINF (Infortuni)</td>
                    <td className="px-4 py-3 text-sm">Solo infortuni non professionali</td>
                    <td className="px-4 py-3 text-center font-bold text-red-600">~1%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-3 font-medium">Imposta alla fonte**</td>
                    <td className="px-4 py-3 text-sm">Solo per lavoratori stranieri</td>
                    <td className="px-4 py-3 text-center font-bold text-red-600">4-15%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-slate-500 mb-6">
              * La percentuale LPP varia in base all'età e al piano pensionistico aziendale.<br />
              ** L'imposta alla fonte varia in base a stipendio, stato civile e figli.
            </p>

            {/* Section 3 - Imposta alla fonte */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
              L'Imposta alla Fonte
            </h2>

            <div className="flex items-start gap-4 bg-amber-50 p-6 rounded-xl mb-6">
              <Building className="w-10 h-10 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-amber-900 mb-2">Cos'è l'imposta alla fonte?</h3>
                <p className="text-amber-800 m-0">
                  È l'imposta sul reddito trattenuta direttamente dal datore di lavoro per i 
                  <strong> lavoratori stranieri</strong> senza permesso C. Include imposte federali, 
                  cantonali e comunali in un'unica aliquota.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3">Fattori che influenzano l'aliquota:</h3>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <TrendingDown className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span><strong>Stipendio lordo:</strong> più alto = aliquota più alta (progressiva)</span>
              </li>
              <li className="flex items-start gap-2">
                <TrendingDown className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span><strong>Stato civile:</strong> coniugati con un solo reddito pagano meno</span>
              </li>
              <li className="flex items-start gap-2">
                <TrendingDown className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span><strong>Figli a carico:</strong> ogni figlio riduce l'aliquota</span>
              </li>
              <li className="flex items-start gap-2">
                <TrendingDown className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span><strong>Comune di residenza/lavoro:</strong> ogni comune ha moltiplicatori diversi</span>
              </li>
            </ul>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
              <h3 className="font-bold text-blue-900 mb-2">Frontalieri italiani</h3>
              <p className="text-blue-800 m-0">
                I frontalieri italiani beneficiano dell'accordo fiscale Italia-Svizzera. Pagano 
                un'imposta alla fonte ridotta (circa <strong>5-8%</strong>) e non devono dichiarare 
                il reddito svizzero in Italia (solo comunicarlo).
              </p>
            </div>

            {/* Section 4 - Practical Example */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold">4</span>
              Esempio Pratico di Calcolo
            </h2>

            <p>
              Vediamo un esempio concreto per un lavoratore con <strong>Permesso G</strong> (frontaliere), 
              celibe, senza figli, con uno stipendio lordo di <strong>CHF 5'000/mese</strong>:
            </p>

            <div className="bg-slate-50 p-6 rounded-xl my-6">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#004E64]" />
                Calcolo Stipendio Netto - Esempio
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <span className="font-medium">Stipendio lordo mensile</span>
                  <span className="font-bold text-lg">CHF 5'000.00</span>
                </div>
                
                <div className="flex justify-between items-center py-2 text-red-600">
                  <span>AVS/AI/IPG (5.30%)</span>
                  <span>- CHF 265.00</span>
                </div>
                <div className="flex justify-between items-center py-2 text-red-600">
                  <span>AD - Disoccupazione (1.10%)</span>
                  <span>- CHF 55.00</span>
                </div>
                <div className="flex justify-between items-center py-2 text-red-600">
                  <span>LPP - Cassa pensione (~7%)</span>
                  <span>- CHF 350.00</span>
                </div>
                <div className="flex justify-between items-center py-2 text-red-600">
                  <span>LAINF - Infortuni (~1%)</span>
                  <span>- CHF 50.00</span>
                </div>
                <div className="flex justify-between items-center py-2 text-red-600 border-b border-slate-200">
                  <span>Imposta alla fonte (~6%)*</span>
                  <span>- CHF 300.00</span>
                </div>
                
                <div className="flex justify-between items-center py-3 bg-emerald-100 px-4 rounded-lg">
                  <span className="font-bold text-emerald-900">STIPENDIO NETTO</span>
                  <span className="font-bold text-2xl text-emerald-700">CHF 3'980.00</span>
                </div>
              </div>
              
              <p className="text-xs text-slate-500 mt-4">
                * L'imposta alla fonte per frontalieri varia. Questo è un esempio indicativo per 
                un celibe senza figli. I coniugati e chi ha figli pagano meno.
              </p>
            </div>

            {/* Summary */}
            <div className="bg-slate-800 text-white p-6 rounded-xl my-8">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <PiggyBank className="w-5 h-5" />
                In sintesi
              </h3>
              <p className="m-0">
                Con uno stipendio lordo di <strong>CHF 5'000</strong>, un frontaliere celibe può 
                aspettarsi un netto di circa <strong>CHF 3'900-4'100</strong> (78-82% del lordo). 
                La percentuale netta aumenta per i coniugati e chi ha figli a carico.
              </p>
            </div>

            {/* Tips */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
              Consigli Utili
            </h2>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Verifica sempre la prima busta paga:</strong> controlla che le trattenute corrispondano alla tua situazione familiare</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Comunica i cambiamenti:</strong> matrimonio, nascita figli, cambio residenza → aggiornamento aliquota</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Considera il 3° pilastro:</strong> risparmio deducibile dalle tasse (fino a CHF 7'056/anno nel 2026)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>13ª mensilità:</strong> molte aziende svizzere pagano 13 stipendi. Verifica nel contratto</span>
              </li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mt-12 pt-8 border-t">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-[#004E64]" />
              Domande Frequenti
            </h2>

            <div className="space-y-4">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Quanto è l'imposta alla fonte in Ticino?</h3>
                  <p className="text-slate-600">
                    L'imposta alla fonte in Canton Ticino varia dal <strong>4% al 15%</strong> circa, 
                    a seconda dello stipendio lordo, stato civile e numero di figli. I frontalieri 
                    italiani pagano generalmente <strong>5-8%</strong> grazie all'accordo fiscale Italia-Svizzera.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Cosa sono AVS e LPP?</h3>
                  <p className="text-slate-600">
                    L'<strong>AVS</strong> (Assicurazione Vecchiaia e Superstiti) è il 1° pilastro del sistema 
                    pensionistico svizzero, mentre la <strong>LPP</strong> (Legge sulla Previdenza Professionale) 
                    è il 2° pilastro. Il lavoratore paga circa il 5.3% per AVS e il 7-8% per LPP.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Come si calcola lo stipendio netto in Svizzera?</h3>
                  <p className="text-slate-600">
                    Dallo stipendio lordo si sottraggono: <strong>AVS/AI/IPG</strong> (5.3%), <strong>AD</strong> (1.1%), 
                    <strong>LPP</strong> (7-8%), <strong>LAINF</strong>, e per gli stranieri l'<strong>imposta alla fonte</strong> 
                    (4-15%). Il netto è circa il <strong>75-85%</strong> del lordo a seconda dei casi.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="mt-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Pronto a candidarti?</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Ora conosci come funzionano gli stipendi in Svizzera. Guarda le ultime offerte 
              di lavoro su LavoroTicino.ch e trova l'opportunità perfetta per te.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/jobs">
                <Button size="lg" className="bg-white text-amber-700 hover:bg-white/90">
                  <Briefcase className="w-5 h-5 mr-2" />
                  Vedi Offerte di Lavoro
                </Button>
              </Link>
              <Link to="/register?type=candidate">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Iscriviti Gratis
                </Button>
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-10 flex items-center justify-between pt-6 border-t">
            <Link to="/guide/cv-svizzero" className="flex items-center gap-2 text-slate-600 hover:text-[#004E64]">
              <ArrowLeft className="w-4 h-4" /> CV Svizzero
            </Link>
            <Link to="/guide-lavoro" className="flex items-center gap-2 text-[#004E64] font-medium hover:underline">
              Tutte le Guide <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
